// Firebase configuration for different environments

// Development Firebase config
const devFirebaseConfig = {
    apiKey: "AIzaSyDY0A80jivlVsI1xPWHXPmSEPFylOyeE9g",
    authDomain: "agroland-mobile-dev.firebaseapp.com",
    projectId: "agroland-mobile-dev",
    storageBucket: "agroland-mobile-dev.firebasestorage.app",
    messagingSenderId: "929642767296",
    appId: "1:929642767296:web:fd7f6e0f034b7cb2ef9e25",
    measurementId: "G-XGJ71MKDBY",
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY_DEV || "BMZebh2ZFm4aT9wK5pCwsgcll66zB-HfkLBAGBl2YQytD26PkN2OMWVtkE38N6cLtlakEkoMbjBNVBRhUfzw_Dc"
};

// Production Firebase config
const prodFirebaseConfig = {
    apiKey: "AIzaSyCpT4nBthyGBe8QWb4dqR5iVJb-1HZhoUg",
    authDomain: "agroland-mobile-app.firebaseapp.com",
    projectId: "agroland-mobile-app",
    storageBucket: "agroland-mobile-app.firebasestorage.app",
    messagingSenderId: "725602347262",
    appId: "1:725602347262:web:305fa7cc51e1b0fff45daf",
    measurementId: "G-HPZR6QLN9R",
    vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY_PROD || process.env.REACT_APP_FIREBASE_VAPID_KEY || "BMZebh2ZFm4aT9wK5pCwsgcll66zB-HfkLBAGBl2YQytD26PkN2OMWVtkE38N6cLtlakEkoMbjBNVBRhUfzw_Dc"
};

// Get Firebase config based on environment
// ALWAYS use production by default since VAPID key and configs are production-based
// Only use development if REACT_APP_FIREBASE_ENV=development is explicitly set
export const firebaseConfig = (() => {
    // Check if explicitly set to development via environment variable
    const explicitlyDevelopment = process.env.REACT_APP_FIREBASE_ENV === 'development';
    
    // Only use development if explicitly set AND on localhost
    const isDevelopment = explicitlyDevelopment &&
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // ALWAYS default to production (unless explicitly set to development)
    const useProduction = !isDevelopment;

    // Firebase config logging removed

    return useProduction ? prodFirebaseConfig : devFirebaseConfig;
})();

// Function to get specific environment config
export function getFirebaseConfig(environment = 'auto') {
    if (environment === 'dev' || environment === 'development') {
        return devFirebaseConfig;
    } else if (environment === 'prod' || environment === 'production') {
        return prodFirebaseConfig;
    } else {
        return firebaseConfig;
    }
}

// Function to force environment (for testing)
export function forceFirebaseEnvironment(environment) {
    if (environment === 'dev' || environment === 'development') {
        return devFirebaseConfig;
    } else if (environment === 'prod' || environment === 'production') {
        return prodFirebaseConfig;
    }
    return firebaseConfig;
}

// Firebase initialization function
export async function initializeFirebase() {
    try {
        // Check if Firebase is available
        if (typeof window !== 'undefined' && window.firebase) {
            const firebase = window.firebase;

            // Initialize Firebase
            if (!firebase.apps || firebase.apps.length === 0) {
                firebase.initializeApp(firebaseConfig);
            }

            return firebase;
        } else {
            return null;
        }
    } catch (error) {
        return null;
    }
}

// Get FCM token
export async function getFirebaseToken() {
    try {
        // Check if VAPID key is valid (not the default placeholder)
        if (!firebaseConfig.vapidKey ||
            firebaseConfig.vapidKey === 'your-prod-vapid-key' ||
            firebaseConfig.vapidKey.length < 20) {
            return null;
        }

        const firebase = await initializeFirebase();
        if (!firebase) {
            return null;
        }

        // Request permission first
        if (!('Notification' in window)) {
            return null;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
            return null;
        }

        // Check if messaging is available
        if (!firebase.messaging) {
            return null;
        }

        // Get messaging instance
        const messaging = firebase.messaging();

        // Get token with VAPID key
        const token = await messaging.getToken({
            vapidKey: firebaseConfig.vapidKey
        });

        return token || null;
    } catch (error) {
        // Silently return null on any Firebase error;
        // callers retry when Firebase becomes ready.
        return null;
    }
}

// Setup FCM token refresh listener
// Note: Firebase compat SDK doesn't have onTokenRefresh, so we use periodic checking
export async function setupTokenRefreshListener(onTokenRefresh) {
    try {
        // Initialize Firebase first if not already initialized
        const firebase = await initializeFirebase();
        if (!firebase) {
            return null;
        }

        // Check if messaging is available
        if (!firebase.messaging) {
            return null;
        }

        const messaging = firebase.messaging();
        
        // Store the last known token
        let lastToken = null;
        
        // Get initial token
        try {
            const initialToken = await messaging.getToken({
                vapidKey: firebaseConfig.vapidKey
            });
            lastToken = initialToken;
        } catch (error) {
            // Ignore initial token errors
        }
        
        // Periodically check for token changes (every 5 minutes)
        // Firebase tokens can refresh, so we check periodically
        const checkInterval = setInterval(async () => {
            try {
                const currentToken = await messaging.getToken({
                    vapidKey: firebaseConfig.vapidKey
                });
                
                // If token changed, call the callback
                if (currentToken && currentToken !== lastToken && lastToken !== null) {
                    if (onTokenRefresh && typeof onTokenRefresh === 'function') {
                        onTokenRefresh(currentToken);
                    }
                }
                
                lastToken = currentToken;
            } catch (error) {
                // Silently handle errors
            }
        }, 5 * 60 * 1000); // Check every 5 minutes

        // Token refresh listener set up
        
        // Return cleanup function
        return {
            messaging,
            cleanup: () => {
                clearInterval(checkInterval);
            }
        };
    } catch (error) {
        return null;
    }
}

// Diagnostic function to check why Firebase token might be empty
export async function diagnoseFirebaseTokenIssue() {
    const diagnostics = {
        hasNotificationSupport: 'Notification' in window,
        notificationPermission: 'Notification' in window ? Notification.permission : 'not_supported',
        hasFirebaseSDK: typeof window !== 'undefined' && !!window.firebase,
        vapidKeyConfigured: false,
        vapidKeyLength: 0,
        firebaseInitialized: false,
        messagingAvailable: false,
        issues: []
    };

    // Check VAPID key
    if (firebaseConfig.vapidKey) {
        diagnostics.vapidKeyConfigured = true;
        diagnostics.vapidKeyLength = firebaseConfig.vapidKey.length;
        if (firebaseConfig.vapidKey === 'your-prod-vapid-key' || firebaseConfig.vapidKey.length < 20) {
            diagnostics.issues.push('VAPID key is not properly configured (placeholder or too short)');
        }
    } else {
        diagnostics.issues.push('VAPID key is missing');
    }

    // Check Firebase SDK
    if (!diagnostics.hasFirebaseSDK) {
        diagnostics.issues.push('Firebase SDK is not loaded (check if scripts are in index.html)');
    }

    // Check notification permission
    if (!diagnostics.hasNotificationSupport) {
        diagnostics.issues.push('Browser does not support notifications');
    } else if (diagnostics.notificationPermission === 'denied') {
        diagnostics.issues.push('Notification permission is denied by user (check browser settings)');
    } else if (diagnostics.notificationPermission === 'default') {
        diagnostics.issues.push('Notification permission not yet requested or denied');
    }

    // Try to initialize Firebase and check messaging
    try {
        const firebase = await initializeFirebase();
        if (firebase) {
            diagnostics.firebaseInitialized = true;
            if (firebase.messaging) {
                diagnostics.messagingAvailable = true;
            } else {
                diagnostics.issues.push('Firebase messaging is not available');
            }
        } else {
            diagnostics.issues.push('Failed to initialize Firebase');
        }
    } catch (error) {
        diagnostics.issues.push(`Firebase initialization error: ${error.message}`);
    }

    return diagnostics;
}

// Setup notification click handler
export function setupNotificationClickHandler() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                if (event.data.url) {
                    window.location.href = event.data.url;
                }
            }
        });
    }
}
